/**
 * Admin → Users network helpers. Thin wrappers over the shared axios
 * instance; every list call takes an AbortSignal so stale searches die.
 *
 *   GET    /userSettings?status=…            admin list (+ counts, adminMeta)
 *   GET    /userSettings?id=…&view=admin     one user (+ adminMeta)
 *   POST   /userSettings                     invite (409 USER_EXISTS)
 *   PATCH  /userSettings                     partial update (server merges)
 *   DELETE /userSettings?id=…&permanent=true delete permanently
 *   GET    /userAdmin/security?id=…          Cognito status / MFA / phone
 *   POST   /userAdmin/resetMfa | resendInvite | bulk
 *   GET    /userAdmin/usage?org=…&days=…     org usage report (PostHog + directory)
 *   GET    /userAdmin/usage/user?id=…&org=…  one person's usage
 */
import { api, adminPasswordReset, getOrgConfig, getReports, getDashboards, getChartOfAccountDepartments } from '../../../utils/api';
import { BASE_URL } from '../../../config/apiConfig';
import { getOrganizations } from '../../../utils/organizationApi';
import { getLocations } from '../../../utils/locationApi';
import { getDepartmentsByOrgId } from '../../../utils/departmentApi';

const USERS_URL = `${BASE_URL}/userSettings`;
const ADMIN_URL = `${BASE_URL}/userAdmin`;

export const isAbortError = (err) => err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED';

/** Best human message from an axios error. */
export function apiErrorMessage(err, fallback = 'Something went wrong.') {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  return data?.error || data?.message || data?.Error || err?.message || fallback;
}

export async function listUsers(query, { signal } = {}) {
  const { data } = await api.get(USERS_URL, { params: query, signal });
  return {
    users: Array.isArray(data?.users) ? data.users : [],
    total: data?.pagination?.total ?? 0,
    counts: data?.counts || null,
  };
}

export async function getUser(id, { signal } = {}) {
  const { data } = await api.get(USERS_URL, { params: { id, view: 'admin' }, signal });
  return data;
}

/** Invite. Rejects with err.code === 'USER_EXISTS' (+ existingId) for a known email. */
export async function createUser(payload) {
  try {
    const { data } = await api.post(USERS_URL, payload);
    return data;
  } catch (err) {
    if (err?.response?.status === 409 && err.response.data?.code === 'USER_EXISTS') {
      const e = new Error(err.response.data.error || 'A user with this email already exists.');
      e.code = 'USER_EXISTS';
      e.existingId = err.response.data.id || null;
      throw e;
    }
    throw err;
  }
}

export async function updateUser(payload) {
  const { data } = await api.patch(USERS_URL, payload);
  return data;
}

export async function setUserDeactivated(id, deactivated) {
  const { data } = await api.patch(USERS_URL, { id, isDeleted: deactivated ? 1 : 0 });
  return data;
}

export async function deleteUserPermanently(id) {
  const { data } = await api.delete(USERS_URL, { params: { id, permanent: 'true' } });
  return data;
}

export async function resetPassword(email, { customPassword = null, sendEmail = true } = {}) {
  return adminPasswordReset(email, customPassword, sendEmail);
}

export async function getUserSecurity(id, { signal } = {}) {
  const { data } = await api.get(`${ADMIN_URL}/security`, { params: { id }, signal });
  return data;
}

export async function resetUserMfa(id) {
  const { data } = await api.post(`${ADMIN_URL}/resetMfa`, { id });
  return data;
}

/** Resolves with { ok, status: 'sent'|'skipped'|'failed', message, temporaryPassword? }. */
export async function resendInvite(id) {
  try {
    const { data } = await api.post(`${ADMIN_URL}/resendInvite`, { id });
    return data;
  } catch (err) {
    if (err?.response?.data?.status) return err.response.data;
    throw err;
  }
}

/** action: addToOrg | setReportAccess | resendInvite | deactivate */
export async function bulkUsers(action, ids, params = {}) {
  const { data } = await api.post(`${ADMIN_URL}/bulk`, { action, ids, ...params });
  return data;
}

/** Usage report for one org. `days` ∈ 7 | 30 | 90 | 365. */
export async function getUsage({ org, days, refresh } = {}, { signal } = {}) {
  const params = { days };
  if (org) params.org = org;
  if (refresh) params.refresh = '1';
  const { data } = await api.get(`${ADMIN_URL}/usage`, { params, signal });
  return data;
}

export async function getUserUsage({ id, org }, { signal } = {}) {
  const { data } = await api.get(`${ADMIN_URL}/usage/user`, { params: { id, org }, signal });
  return data;
}

// ── Reference data ────────────────────────────────────────────────────────

/**
 * Organizations (SQL names + ids, org-config workspaces) and all locations
 * the admin can see. Failures degrade to empty lists.
 */
export async function loadDirectory() {
  const [orgs, configs, locations] = await Promise.all([
    getOrganizations().catch(() => []),
    getOrgConfig().catch(() => []),
    getLocations().catch(() => []),
  ]);
  return {
    orgs: Array.isArray(orgs) ? orgs : [],
    configs: Array.isArray(configs) ? configs : [],
    locations: Array.isArray(locations) ? locations : [],
  };
}

export const fetchLaborDepartments = (dbOrgId) => getDepartmentsByOrgId(dbOrgId).then((r) => (Array.isArray(r) ? r : [])).catch(() => []);
export const fetchGlDepartments = (dbOrgId) => getChartOfAccountDepartments(dbOrgId).then((r) => (Array.isArray(r) ? r : [])).catch(() => []);

const withTimeout = (promise, ms) => Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), ms))]);

/** Power BI workspace inventory — only fetched at save time, only for orgs whose report groups changed. */
export async function fetchPbiInventory(workspaceId) {
  if (!workspaceId) return { reports: null, dashboards: null };
  const unwrap = (r) => (Array.isArray(r) ? r : Array.isArray(r?.value) ? r.value : null);
  const [reports, dashboards] = await Promise.all([
    withTimeout(getReports(workspaceId).then(unwrap).catch(() => null), 8000),
    withTimeout(getDashboards(workspaceId).then(unwrap).catch(() => null), 8000),
  ]);
  return { reports, dashboards };
}
